import React, { useState, useRef } from 'react';
import Card from './Card';
import AppContainer from '../Container/AppContainer';
import Typography from '@material-ui/core/Typography';
import T from 'i18n-react';
import Grid from '@material-ui/core/Grid';
import Pagination from '../../core/Pagination';
import makeStyles from '@material-ui/core/styles/makeStyles';
import RegionSelect from '../Select/RegionSelect';
import isEqual from 'lodash/isEqual';
import { BUSINESSES } from '../routing';
import Button from '@material-ui/core/Button';
import AuthDialogContextProvider from '../components/AuthDialog/AuthDialogContextProvider';

const useStyles = makeStyles((theme) => ({
  typography: {
    marginBottom: theme.spacing(2),
  },
  feature: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      fontSize: theme.typography.pxToRem(27),
    },
  },
  pagination: {
    marginTop: theme.spacing(8),
  },
  region: {
    marginBottom: theme.spacing(4),
  },
  grid: {
    justifyContent: 'center',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'flex-start',
    },
  },
  clear: {
    color: theme.palette.text.secondary,
    textDecoration: 'underline',
    '&:hover': {
      color: theme.palette.text.secondary,
      backgroundColor: 'transparent',
    },
  },
  lgItem: {
    [theme.breakpoints.up('lg')]: {
      maxWidth: '25%',
      flexBasis: '25%',
    },
  },
}));

type BusinessesProps = {
  businesses?: Array<Object>;
  regions?: Array<{ id: number; name: string }>;
  isAuthenticated?: boolean;
  totalPages?: number;
  featureName?: string | null;
};

const Businesses = ({
  businesses: businessesProps = [],
  isAuthenticated,
  totalPages = 0,
  featureName,
  regions,
}: BusinessesProps) => {
  const getHistoryBusinesses = () => window.history?.state?.businesses;

  const [businesses] = useState(() => {
    const historyBusinesses = getHistoryBusinesses();
    return Array.isArray(historyBusinesses)
      ? historyBusinesses
      : businessesProps;
  });

  const getRegionIds = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const ids = searchParams.get('region')?.split?.(',');
    if (!Array.isArray(ids)) {
      return [];
    }
    return ids.map((id) => parseInt(id)).filter((id) => id > 0);
  };

  const initialRegionId = useRef(getRegionIds());

  const [regionId, setRegionId] = useState(() =>
    Array.isArray(initialRegionId.current) ? initialRegionId.current : [],
  );

  const classes = useStyles();

  const handleFollowChange = (id, follow, count) => {
    const historyBusinesses = getHistoryBusinesses();

    const nextBusinesses = [
      ...(Array.isArray(historyBusinesses) ? historyBusinesses : businesses),
    ];

    const business = nextBusinesses.find(
      ({ id: businessId }) => businessId === id,
    );

    if (business) {
      business.isFollowing = follow;
      business.followerCount = count;
      // Store following state
      window.history?.replaceState?.(
        { ...(window.history.state || {}), businesses: nextBusinesses },
        '',
      );
    }
  };

  return (
    <AuthDialogContextProvider isLoggedIn={!!isAuthenticated}>
      <AppContainer>
        <div className={classes.region}>
          <Grid
            container
            alignItems="center"
            spacing={1}
            className={classes.grid}
          >
            <Grid item style={{ width: '100%', maxWidth: 300 }}>
              <RegionSelect
                regions={regions}
                multiple
                value={regionId}
                onChange={(event) =>
                  setRegionId(event.target.value.filter(Boolean))
                }
                onClose={(event) => {
                  if (
                    !isEqual(initialRegionId.current.sort(), regionId.sort())
                  ) {
                    window.location.href =
                      BUSINESSES +
                      (regionId.length > 0
                        ? `?region=${regionId.join(',')}`
                        : '');
                  }
                }}
                label={T.translate('businesses.filterByRegion')}
              />
            </Grid>
            {regionId.length > 0 && (
              <Grid item>
                <Button
                  className={classes.clear}
                  onClick={() => {
                    window.location.href = BUSINESSES;
                  }}
                  disableRipple
                >
                  {T.translate('clear')}
                </Button>
              </Grid>
            )}
          </Grid>
        </div>
        {featureName && (
          <Typography
            color="textPrimary"
            gutterBottom
            variant="h3"
            className={classes.feature}
          >
            {T.translate('businesses.feature', { shortName: featureName })}
          </Typography>
        )}
        <Typography variant="body1" className={classes.typography}>
          {T.translate('businesses.indexSubtitle')}
        </Typography>
        <Grid container alignItems="stretch" spacing={2}>
          {businesses.map((business, index) => (
            <Grid
              item
              xs={6}
              sm={4}
              md={3}
              lg={2}
              key={`business${index}`}
              className={classes.lgItem}
            >
              <Card
                {...business}
                isAuthenticated={isAuthenticated}
                onChange={handleFollowChange}
              />
            </Grid>
          ))}
        </Grid>
        {(totalPages > 1 || businesses.length > 0) && (
          <div className={classes.pagination}>
            <Pagination count={totalPages} />
          </div>
        )}
      </AppContainer>
    </AuthDialogContextProvider>
  );
};

export default Businesses;
